// PO Settlement — read-only AP aggregation per Purchase Order (#4344/#4345).
// Mirrors Finance.Models PoSettlement* DTOs and the Order.Models PO enums.
import { ApDocumentStatus, ApDocumentType, Currency } from '../ap-document/ap-document';
import { ApPaymentStatus } from '../ap-payment/ap-payment';
import { ApPaymentRequestStatus } from '../ap-payment-request/ap-payment-request';
import { Translation } from '../Translations';

export { Currency } from '../ap-document/ap-document';

/** PurchaseOrder lifecycle status (Order.Models PurchaseOrderStatus). */
export enum PurchaseOrderStatus {
  Draft = 0,
  Submitted = 1,
  Approved = 2,
  Receiving = 3,
  PartiallyReceived = 4,
  Received = 5,
  Rejected = 6,
  Cancelled = 7,
  InTransit = 8,
}

/** Persisted PO PaymentStatus (Order.Models PurchaseOrderPaymentStatus, #4343). */
export enum PurchaseOrderPaymentStatus {
  Unpaid = 0,
  PartiallyPaid = 1,
  Paid = 2,
  Settled = 3,
}

export enum PurchaseOrderType {
  Standard = 0,
  Yarn = 1,
  Chemicals = 2,
  /** Confidential purchasing (#5942): Order.API only returns these to role holders. */
  Administrative = 3,
}

/** Finer dashboard settlement status (Finance.Models PoSettlementDisplayStatus). */
export enum PoSettlementDisplayStatus {
  NoDocuments = 0,
  Unpaid = 1,
  PaymentRequested = 2,
  PartiallyPaid = 3,
  Settled = 4,
}

export enum PoSettlementActivityKind {
  PaymentRequested = 1,
  PaymentPosted = 2,
  /** A posted vendor advance earmarked to this PO (#4346–#4350). */
  AdvancePosted = 3,
}

export interface PoSettlementRow {
  purchaseOrderId: string;
  purchaseOrderNumber?: string | null;
  providerReference?: string | null;
  providerName: string;
  issuedDate: string;
  currency: Currency;
  orderStatus: number;
  paymentStatus: number;
  displayStatus: PoSettlementDisplayStatus;
  documentCount: number;
  ordered: number;
  invoiced: number;
  requested: number;
  paid: number;
  /** Withheld from this PO's invoices by posted Constancias de Retención (#4454) — money that discharges
   *  the payable and goes to SAR instead of the provider. Counted as settled alongside cash and the advance
   *  applied, and subtracted from `remaining`: the provider will never be paid it. */
  withheld: number;
  /** Advance DRAWN DOWN against this PO's invoices — the part that has actually settled AP, so it (not
   * advanceAllocated) is what `remaining` subtracts (#4346–#4350). */
  advanceApplied: number;
  /** Advance COMMITTED to this PO: cash has left the bank but has not yet been drawn down against an
   * invoice. Reported separately because "we advanced them 7,000" and "7,000 of the invoices are settled"
   * are different claims. */
  advanceAllocated: number;
  remaining: number;
  settlementPercent: number;
}

export interface PoSettlementProviderSummary {
  providerReference?: string | null;
  providerName: string;
  poCount: number;
  ordered: number;
  invoiced: number;
  requested: number;
  paid: number;
  balance: number;
  settledCount: number;
  percentPaid: number;
}

export interface PoSettlementSummary {
  totalCount: number;
  requestedPoCount: number;
  settledCount: number;
  totalOrdered: number;
  totalInvoiced: number;
  totalRequested: number;
  totalPaid: number;
  openBalance: number;
  currency: Currency;
  byProvider: PoSettlementProviderSummary[];
}

export interface PoSettlementRequestRef {
  id: string;
  number: string;
  date?: string | null;
  actor: string;
  status: ApPaymentRequestStatus;
  amount: number;
}

export interface PoSettlementPaymentRef {
  id: string;
  number: string;
  date?: string | null;
  actor: string;
  status: ApPaymentStatus;
  amount: number;
  currency: Currency;
  bankName?: string | null;
  bankAccountMasked?: string | null;
}

export interface PoSettlementDocument {
  id: string;
  number: string;
  documentType: ApDocumentType;
  status: ApDocumentStatus;
  fiscalNumber?: string | null;
  documentDate: string;
  dueDate: string;
  total: number;
  /** Cash paid against this document. */
  paid: number;
  /** Withheld from it by a posted Constancia (#4454) — neither paid nor still owed. */
  withheld: number;
  /** What the document still owes — its own balanceDue, net of cash, withholding and any advance applied.
   *  Not total − paid, which reported a withheld invoice as outstanding for money nobody will ever pay. */
  balance: number;
  requests: PoSettlementRequestRef[];
  payments: PoSettlementPaymentRef[];
}

export interface PoSettlementActivity {
  kind: PoSettlementActivityKind;
  date: string;
  referenceNumber: string;
  documentNumber: string;
  actor: string;
  amount: number;
}

export interface PoSettlementDetail {
  purchaseOrderId: string;
  purchaseOrderNumber?: string | null;
  providerReference?: string | null;
  providerName: string;
  issuedDate: string;
  currency: Currency;
  orderStatus: number;
  paymentStatus: number;
  displayStatus: PoSettlementDisplayStatus;
  ordered: number;
  invoiced: number;
  requested: number;
  paid: number;
  /** Withheld from this PO's invoices by posted Constancias de Retención (#4454) — money that discharges
   *  the payable and goes to SAR instead of the provider. Counted as settled alongside cash and the advance
   *  applied, and subtracted from `remaining`: the provider will never be paid it. */
  withheld: number;
  /** Advance drawn down against this PO's invoices — see PoSettlementRow.advanceApplied. */
  advanceApplied: number;
  /** Advance committed but not yet drawn down — see PoSettlementRow.advanceAllocated. */
  advanceAllocated: number;
  remaining: number;
  documents: PoSettlementDocument[];
  activity: PoSettlementActivity[];
  /** The posted advances earmarked to this PO, so the page can show which ones make up the totals. */
  advances: PoSettlementAdvance[];

  /** What actually arrived against this PO (#5235) — the goods side beside the money side. */
  goodsReceipts: PoSettlementGoodsReceipt[];
}

/** One Goods Receipt Note booked against the PO (#5235). */
export interface PoSettlementGoodsReceipt {
  id: string;
  /** The GRN's business number, e.g. "GR-NT-2403". */
  number: string;
  /**
   * When the receipt was completed. Derived server-side from the GRN's status history — the GRN's own
   * `receivedDate` field is null on every record.
   *
   * A pure date: render it with `| date: 'dd/MM/yyyy' : 'UTC'`. Without the explicit UTC it is converted
   * to the browser's zone and a UTC-6 client shows the day before (#5234).
   */
  receivedDate?: string | null;
  status: number;
  isCompleted: boolean;
  locationCode?: string | null;
  receiverUsername?: string | null;
  deliveryNote?: string | null;
  totalQuantity: number;
  lines: PoSettlementGoodsReceiptLine[];
}

/** One item line on a Goods Receipt Note (#5235). */
export interface PoSettlementGoodsReceiptLine {
  itemCode: string;
  itemDisplayName?: Translation | null;
  quantity: number;
  unitOfMeasure: number;
}

/** A posted vendor advance earmarked to the PO (#4346–#4350). */
export interface PoSettlementAdvance {
  id: string;
  number: string;
  date?: string | null;
  actor: string;
  currency: Currency;
  /** Earmarked to this PO by this advance. */
  allocated: number;
  /** Already drawn down against this PO's invoices. */
  applied: number;
  /** Still available to draw down = allocated − applied. */
  remaining: number;
}

/** Mirrors Order.Models PurchaseOrderSearch (subset used by the settlement dashboard). */
export interface PurchaseOrderSettlementSearch {
  purchaseOrderNumber?: string | null;
  providerReference?: string | null;
  status?: number[] | null;
  paymentStatus?: number[] | null;
  type?: number | null;
}
